import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import { UserAuth } from 'Modals/UserAuth';
import { newServer } from 'constants/constants';
import { PROVIDER_STORAGE_KEY, ProviderSelection } from 'constants/providers';
import React, { useEffect, createContext, useContext, useState } from 'react';

interface EnrollContextType {
  saveEnrrollData: (data: any) => void;
  saveEnrrollDataUsio: (data: any) => void;
  setStateDwolla: (data: number) => void;
  setStateUsio: (data: number) => void;
  /**
   * Proveedor de pago elegido en el onboarding.
   * - `'usio' | 'dwolla' | 'both'` -> el abogado entrega su KYC en este flujo.
   * - `null` -> "configurar despues": la solicitud se crea sin proveedor y el
   *   abogado completa el KYC desde `/payment-setup` en su subdominio.
   */
  providerChoice: ProviderSelection | null;
  setProviderChoice: (choice: ProviderSelection | null) => void;
  enrollData: any;
  enrollDataUsio: any;
  stateDwolla: number;
  stateUsio: number;
}

/**
 * Lee la eleccion guardada.
 *
 * Se persiste en localStorage porque el wizard puede recargarse a mitad de
 * camino (login, redirecciones externas del KYC) y perder la eleccion obligaria
 * a repetir el paso.
 *
 * Se limpia cuando el usuario cierra sesion (ver `logout` en AuthProvider) para
 * que la siguiente persona en el mismo navegador no herede la eleccion.
 */
const readStoredChoice = (): ProviderSelection | null => {
  try {
    return localStorage.getItem(PROVIDER_STORAGE_KEY) as ProviderSelection | null;
  } catch (error) {
    // Modo privado o storage bloqueado: seguimos sin persistencia.
    return null;
  }
};

const persistChoice = (choice: ProviderSelection | null) => {
  try {
    if (choice === null) {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
    } else {
      localStorage.setItem(PROVIDER_STORAGE_KEY, choice);
    }
  } catch (error) {
    // Igual que arriba: no poder persistir no debe romper el flujo.
  }
};

const EnrollContext = createContext<EnrollContextType>({
  enrollData: null,
  enrollDataUsio: null,
  stateDwolla: 1,
  setStateDwolla: (data: any) => {},
  saveEnrrollDataUsio: () => {},
  saveEnrrollData: (data: any) => {},
  setStateUsio: (data: any) => {},
  stateUsio: 0,
  providerChoice: null,
  setProviderChoice: () => {},
});

export const useEnrollForm = () => {
  return useContext(EnrollContext);
};

export const EnrollProvider: any = ({ children }: any) => {
  const [enrollData, setEnrollData] = useState({});
  const [enrollDataUsio, setEnrollDataUsio] = useState({});
  const [stateDwolla, setStateDwolla] = useState<number>(0);
  const [stateUsio, setStateUsio] = useState<number>(0);
  const [providerChoice, setProviderChoiceState] = useState<ProviderSelection | null>(readStoredChoice);
  const { user } = useLogin();

  /**
   * Propósito: Sincroniza la eleccion en memoria con la sesión.
   * Al cerrar sesión se limpia para que el siguiente login no herede
   * "configure later". Al hidratar/iniciar sesión se relee localStorage.
   */
  useEffect(() => {
    if (!user?.token) {
      /**
       * Output:
       * Side effects — Limpia la eleccion en memoria sin tocar localStorage
       *   (AuthProvider.logout ya borra la clave; no persistir null aquí
       *   evitaría perderla antes de hidratar el token)
       */
      setProviderChoiceState(null);
      return;
    }

    /**
     * Output:
     * Side effects — Restaura la eleccion guardada para reanudar el wizard
     */
    setProviderChoiceState(readStoredChoice());
  }, [user?.token, user?.user_id]);

  /**
   * Propósito: Guarda la eleccion de proveedor en memoria y localStorage.
   */
  const setProviderChoice = (choice: ProviderSelection | null) => {
    setProviderChoiceState(choice);
    /**
     * Output:
     * Side effects — Persiste o borra `enrollProviderChoice`
     */
    persistChoice(choice);
  };

  const saveEnrrollData = (data: any) => {
    setEnrollData((res: any) => {
      const updateData = { ...res, data };
      return updateData;
    });
  };

  const saveEnrrollDataUsio = (data: any) => {
    setEnrollDataUsio((res: any) => {
      const updateData = { ...res, data };
      return updateData;
    });
  };

  return (
    // @ts-ignore
    <EnrollContext.Provider
      value={{
        enrollData,
        enrollDataUsio,
        saveEnrrollData,
        saveEnrrollDataUsio,
        setStateDwolla,
        stateDwolla,
        setStateUsio,
        stateUsio,
        providerChoice,
        setProviderChoice,
      }}
    >
      {children}
    </EnrollContext.Provider>
  );
};
